import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { IconBuilding, IconAlertTriangle } from '@tabler/icons-react';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const PBMPayerPerformance = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getPBMPayerPerformance({
          organization,
          dateRange,
          limit: 10,
        });
        setData(result);
      } catch (err) {
        console.error('Failed to fetch PBM/Payer performance:', err);
        setError('Failed to load PBM/Payer performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">PBM & Payer Performance Comparison</CardTitle>
          <CardDescription className="text-sm">
            Coverage rates and response times by top PBMs and Payers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-96" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">PBM & Payer Performance Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No PBM/Payer performance data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const getCoverageColor = (value) => {
    if (value >= 80) return 'default';
    if (value >= 50) return 'warning';
    return 'destructive';
  };

  const getResponseTimeColor = (value) => {
    if (value < 1000) return 'default'; // < 1 second
    if (value < 3000) return 'warning'; // < 3 seconds
    return 'destructive'; // >= 3 seconds
  };

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <IconBuilding size={20} />
          <div className="flex-1">
            <CardTitle className="text-base font-bold">PBM & Payer Performance Comparison</CardTitle>
            <CardDescription className="text-sm">
              Coverage rates and response times by top PBMs and Payers
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PBM Performance Table */}
          <div>
            <h6 className="text-sm font-semibold mb-3">Top PBMs by Check Volume</h6>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">PBM</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Checks</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Coverage Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">PA Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg Response</th>
                  </tr>
                </thead>
                <tbody>
                  {(!data.top_pbms || data.top_pbms.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground">
                        No PBM data available
                      </td>
                    </tr>
                  ) : (
                    data.top_pbms.map((pbm, index) => (
                      <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium">{pbm.pbm_name || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm">{pbm.check_count.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={getCoverageColor(pbm.coverage_rate)}>
                            {pbm.coverage_rate.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm text-orange-600 font-medium">
                            {pbm.pa_rate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={getResponseTimeColor(pbm.avg_response_time_ms)}>
                            {(pbm.avg_response_time_ms / 1000).toFixed(2)}s
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {data.top_pbms && data.top_pbms.length > 0 && (
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-semibold mb-2 text-muted-foreground">Insights</p>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>Total PBMs analyzed: <span className="font-semibold text-foreground">{data.top_pbms.length}</span></div>
                  <div>Avg coverage rate: <span className="font-semibold text-foreground">
                    {(data.top_pbms.reduce((sum, pbm) => sum + pbm.coverage_rate, 0) / data.top_pbms.length).toFixed(1)}%
                  </span></div>
                  <div>Avg PA rate: <span className="font-semibold text-foreground">
                    {(data.top_pbms.reduce((sum, pbm) => sum + pbm.pa_rate, 0) / data.top_pbms.length).toFixed(1)}%
                  </span></div>
                </div>
              </div>
            )}
          </div>

          {/* Payer Performance Table */}
          <div>
            <h6 className="text-sm font-semibold mb-3">Top Payers by Check Volume</h6>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payer</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Checks</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Coverage Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">PA Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg Response</th>
                  </tr>
                </thead>
                <tbody>
                  {(!data.top_payers || data.top_payers.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground">
                        No Payer data available
                      </td>
                    </tr>
                  ) : (
                    data.top_payers.map((payer, index) => (
                      <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium">{payer.payer_name || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm">{payer.check_count.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={getCoverageColor(payer.coverage_rate)}>
                            {payer.coverage_rate.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm text-orange-600 font-medium">
                            {payer.pa_rate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={getResponseTimeColor(payer.avg_response_time_ms || 0)}>
                            {((payer.avg_response_time_ms || 0) / 1000).toFixed(2)}s
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {data.top_payers && data.top_payers.length > 0 && (
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-semibold mb-2 text-muted-foreground">Insights</p>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>Total Payers analyzed: <span className="font-semibold text-foreground">{data.top_payers.length}</span></div>
                  <div>Avg coverage rate: <span className="font-semibold text-foreground">
                    {(data.top_payers.reduce((sum, payer) => sum + payer.coverage_rate, 0) / data.top_payers.length).toFixed(1)}%
                  </span></div>
                  <div>Avg PA rate: <span className="font-semibold text-foreground">
                    {(data.top_payers.reduce((sum, payer) => sum + payer.pa_rate, 0) / data.top_payers.length).toFixed(1)}%
                  </span></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Legend:</span>
            {' '}Coverage Rate: <Badge variant="default" className="mx-1">≥80%</Badge>
            <Badge variant="warning" className="mx-1">50-79%</Badge>
            <Badge variant="destructive" className="mx-1">&lt;50%</Badge>
            {' '}| Response Time: <Badge variant="default" className="mx-1">&lt;1s</Badge>
            <Badge variant="warning" className="mx-1">1-3s</Badge>
            <Badge variant="destructive" className="mx-1">&gt;3s</Badge>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PBMPayerPerformance;
